import React, { lazy, useMemo, Suspense } from "react";
import PropTypes from "prop-types";
import {
  Navigate,
  RouterProvider as ReactRouterProvider,
  createBrowserRouter,
  useParams,
} from "react-router-dom";

import useAuth from "./hooks/useAuth";
import useUser from "./hooks/useUser";

import Loader from "./components/ui/Loader";
import ErrorPage from "./pages/ErrorPage";

const EditEmployee = lazy(() => import("./pages/admin/EditEmployee"));
const EditOrganization = lazy(() => import("./pages/admin/EditOrganization"));
const ViewOrganization = lazy(() => import("./pages/ViewOrganization"));
const DefaultLayout = lazy(() => import("./layouts/DefaultLayout"));
const NotAuthorized = lazy(() => import("./pages/NotAuthorized"));
const MainLayout = lazy(() => import("./layouts/MainLayout"));

const EditPDF = lazy(() => import("./pages/EditPDF"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const About = lazy(() => import("./pages/About"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const InfographicsPage = lazy(() => import("./pages/InfographicsPage"));
const Profile = lazy(() => import("./pages/Profile"));
const Library = lazy(() => import("./pages/Documents"));
const TemplateLibrary = lazy(() => import("./pages/TemplateDocuments"));
const TailorPDF = lazy(() => import("./pages/TailorPDF"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AdminIndex = lazy(() => import("./pages/admin/Index"));
const AuthIndex = lazy(() => import("./pages/auth/Index"));
const SignIn = lazy(() => import("./components/auth/SignIn"));
const Organizations = lazy(() => import("./pages/admin/Organizations"));
const Employees = lazy(() => import("./pages/admin/Employees"));
const Template = lazy(() => import("./pages/admin/Template"));
const Styles = lazy(() => import("./pages/admin/Styles"));
const Batches = lazy(() => import("./pages/batches/Batches"));
const BatchDetail = lazy(() => import("./pages/batches/BatchDetail"));
const TemplateCatalog = lazy(() => import("./pages/bundles/TemplateCatalog"));
const BundleDetail = lazy(() => import("./pages/bundles/BundleDetail"));
const CatalogNew = lazy(() => import("./pages/bundles/CatalogNew"));
// /catalog-new/:bundleId -> /catalog/:bundleId
const CatalogNewRedirect = () => {
  const { bundleId } = useParams();
  return <Navigate to={bundleId ? `/catalog/${bundleId}` : "/catalog"} replace />;
};

const findIntersection = (A = [], B = []) => {
  return A.filter((x) => B.includes(x)).length > 0;
};

const routeFabric = ({
  path,
  element: Element,
  isAuth,
  userRoles = [],
  meta,
  routes,
  index,
  exact,
}) => {
  const { guarded, authRedirect, allowedRoles = [] } = meta || {};
  let ResultElement;
  let children = null;
  const key = path || "key_index";

  if (routes && Array.isArray(routes)) {
    children = routes.map((opts) => routeFabric({ isAuth, ...opts }));
  }
  if (guarded) {
    if (!isAuth) {
      ResultElement = <Navigate to="/auth/login" />;
    } else {
      if (allowedRoles?.length) {
        const allowed = findIntersection(allowedRoles, userRoles);
        ResultElement = allowed ? Element : <NotAuthorized />;
      } else {
        ResultElement = Element;
      }
    }
  } else if (authRedirect) {
    ResultElement = !isAuth ? Element : <Navigate to="/" />;
  } else {
    ResultElement = Element;
  }

  return {
    path,
    index,
    exact,
    key,
    element: <Suspense fallback={<Loader />}>{ResultElement}</Suspense>,
    children: children,
  };
};

const routesInfo = [
  { path: "*", element: <NotFoundPage /> },

  { path: "/", element: <Dashboard />, meta: { guarded: true } },
  { path: "/profile", element: <Profile />, meta: { guarded: true } },
  {
    path: "/template",
    element: <Template />,
    meta: { guarded: true },
    allowedRoles: ["super"],
  },
  {
    path: "/styles",
    element: <Styles />,
    meta: { guarded: true },
    allowedRoles: ["super"],
  },
  { path: "/about", element: <About />, meta: { guarded: true } },
  {
    path: "/system/template-catalog",
    element: <TemplateCatalog />,
    meta: { guarded: true, allowedRoles: ["super"] },
  },
  {
    path: "/system/template-catalog/:bundleId",
    element: <BundleDetail />,
    meta: { guarded: true, allowedRoles: ["super"] },
  },
  { path: "/batches", element: <Batches />, meta: { guarded: true } },
  { path: "/batches/:batchId", element: <BatchDetail />, meta: { guarded: true } },
  { path: "/gallery", element: <GalleryPage />, meta: { guarded: true } },
  { path: "/infographics", element: <InfographicsPage />, meta: { guarded: true } },
  {
    path: "auth/forgotpass",
    exact: true,
    element: <ForgotPassword />,
    meta: { authRedirect: true },
  },
  {
    path: "auth/resetpassword",
    exact: true,
    element: <ResetPassword />,
    meta: { authRedirect: true },
  },
  {
    path: "auth",
    element: <AuthIndex />,
    meta: { authRedirect: true },
    routes: [
      { path: "login", element: <SignIn />, index: true, meta: { authRedirect: true } },
    ],
  },
  {
    path: "admin",
    element: <AdminIndex />,
    meta: { guarded: true, allowedRoles: ["super", "admin"] },
    routes: [
      {
        index: true,
        path: "organizations",
        element: <Organizations />,
      },
      {
        index: true,
        path: "organizations/employees",
        element: <Employees />,
      },
      {
        index: true,
        path: "organization",
        element: <EditOrganization />,
      },
      {
        index: true,
        path: "organizations/:orgId/employee/:employeeId",
        element: <EditEmployee />,
      },
    ],
  },
  { path: "organization", element: <ViewOrganization />, meta: { guarded: true } },
  // Catalog = the organization's bundles (formerly "Catalog New").
  {
    path: "catalog",
    element: <CatalogNew />,
    meta: { guarded: true, allowedRoles: ["super", "admin", "employee"] },
  },
  {
    path: "catalog/:bundleId",
    element: <BundleDetail readOnly listPath="/catalog" listLabel="Catalog" />,
    meta: { guarded: true, allowedRoles: ["super", "admin", "employee"] },
  },
  // Old /catalog-new links keep working.
  { path: "catalog-new", element: <Navigate to="/catalog" replace /> },
  { path: "catalog-new/:bundleId", element: <CatalogNewRedirect /> },
  {
    path: "template-catalog",
    element: <TemplateLibrary type="all" />,
    meta: { guarded: true, allowedRoles: ["super", "admin", "employee"] },
  },
  { path: "my-folder", element: <Library type="my" />, meta: { guarded: true } },
  {
    path: "team-folder",
    element: <Library type="team" />,
    meta: { guarded: true },
  },
  {
    path: "/:folderType/document/:documentId/tailorpdf",
    element: <TailorPDF />,
    meta: { guarded: true },
  },
  {
    path: "/:folderType/document/:documentId/editpdf",
    element: <EditPDF />,
    meta: { guarded: true },
  },
];

const Layout = React.memo(function Layout({ isAuth }) {
  return isAuth ? <MainLayout /> : <DefaultLayout />;
});

Layout.propTypes = {
  isAuth: PropTypes.bool.isRequired,
};

export const RouterProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { userRoles, isLoading: userLoading } = useUser();
  const isAuth = isAuthenticated();

  const isLoading = isAuth ? userLoading : authLoading;

  const routes = useMemo(() => {
    return isLoading
      ? [{ path: "*", element: <Loader /> }]
      : routesInfo.map((opts) => routeFabric({ isAuth, userRoles, ...opts }));
  }, [isLoading, isAuth, userRoles]);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: (
            <Suspense fallback={<Loader />}>
              <Layout isAuth={isAuth} />
            </Suspense>
          ),
          errorElement: <ErrorPage />,
          children: routes,
        },
      ]),
    [isAuth, routes],
  );

  return <ReactRouterProvider router={router}>{children}</ReactRouterProvider>;
};

RouterProvider.propTypes = {
  children: PropTypes.node,
};
